import React, { useState } from 'react'
import { makeStyles } from '@material-ui/core'
import type { Theme } from '@material-ui/core/styles'
import * as Sentry from '@sentry/react'
import { useSnackbar } from 'notistack'
import { getObjectLabels } from '@paintscout/util/builder'
import { useSaveLocationOptionsMutation } from '@paintscout/api'
import type { StyleClasses } from '../styles'
import type { DialogProps } from '../components/Dialog'
import type { DropdownSelectOption } from '../components/DropdownSelect'
import Button from '../components/Button'
import Dialog, { DialogActions, DialogContent, DialogTitle } from '../components/Dialog'
import Typography from '../components/Typography'
import DropdownSelect from '../components/DropdownSelect'
import { useClientOptions } from '../providers/ClientOptionsProvider'
import Checkbox from '../components/Checkbox'

const useStyles = makeStyles<Theme, TransferToLocationDialogProps>(
  (_theme) => ({
    root: {
      overflow: 'visible'
    },
    dialogTitle: {},
    dialogContent: {
      minHeight: '200px',
      overflow: 'visible'
    },
    dialogActions: {
      overflow: 'visible'
    },
    cancel: {},
    confirm: {}
  }),
  { name: 'TransferToLocationDialog' }
)

export interface TransferToLocationDialogProps extends DialogProps {
  classes?: DialogProps['classes'] & StyleClasses<typeof useStyles>
  locationName: string
  locationId: string
  onConfirm: () => any
  onCancel: (event: React.MouseEvent<HTMLElement>) => any
}

function TransferToLocationDialog(props: TransferToLocationDialogProps) {
  const classes = useStyles(props)
  const { onConfirm, onCancel, locationName, locationId, ...baseDialogProps } = props
  const { enqueueSnackbar } = useSnackbar()
  const [isLoading, setIsLoading] = useState(false)
  const { options } = useClientOptions()
  const objectLabels = getObjectLabels({ options })
  const [saveLocationOptions] = useSaveLocationOptionsMutation()
  const [selectedValues, setSelectedValues] = useState<DropdownSelectOption[]>([])
  const [overwriteExisting, setOverwriteExisting] = useState(false)

  const leftButton = (
    <Button className={classes.cancel} disabled={isLoading} variant="textNoPadding" onClick={onCancel}>
      {'Cancel'}
    </Button>
  )

  const transferOptions: DropdownSelectOption[] = [
    {
      key: 'rateSections',
      label: 'Rates',
      value: 'rateSections'
    },
    {
      key: 'intExtOptions',
      label: `${objectLabels.quote.value} Types`,
      value: 'intExtOptions'
    },
    {
      key: 'products',
      label: `Products`,
      value: 'products'
    },
    {
      key: 'areaTemplates',
      label: `Area Names`,
      value: 'areaTemplates'
    },
    {
      key: 'lineItems',
      label: `Line Items`,
      value: 'lineItems'
    },
    {
      key: 'presentationOptions',
      label: `Presentation Options`,
      value: 'presentationOptions'
    },
    {
      key: 'terms',
      label: `Terms`,
      value: 'terms'
    }
  ]

  return (
    <Dialog {...baseDialogProps} classes={{ root: classes.root, paper: classes.root }}>
      <DialogTitle classes={{ root: classes.dialogTitle }}>
        <Typography variant="h2">Transfer To Location</Typography>
      </DialogTitle>
      <DialogContent classes={{ root: classes.dialogContent }}>
        <Typography variant="body1">
          Select the options sections you would like to transfer to {locationName}
        </Typography>
        <br />
        <DropdownSelect
          searchable
          label={'Select Values'}
          options={transferOptions}
          variant="multi"
          placeholder={{ plural: 'Select Values' }}
          value={selectedValues}
          onChange={(values) => setSelectedValues(values)}
        />
        <br />
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', paddingTop: '1em' }}>
          <Checkbox
            checked={overwriteExisting}
            onChange={() => setOverwriteExisting(!overwriteExisting)}
            style={{ paddingLeft: 0 }}
          />
          <Typography variant="body1">Overwrite existing values?</Typography>
        </div>
      </DialogContent>
      <DialogActions classes={{ root: classes.dialogActions }} leftButton={leftButton}>
        <Button
          className={classes.confirm}
          form="transfer-to-location-dialog-form"
          variant={'contained'}
          disabled={isLoading || selectedValues.length === 0}
          data-testid="transfer-to-location-dialog-confirm-button"
          onClick={handleSubmit}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  )

  async function handleSubmit() {
    setIsLoading(true)
    console.log({ selectedValues })
    try {
      await saveLocationOptions({
        variables: {
          options: {
            ...selectedValues.reduce((acc, value) => {
              acc[value.key] = true
              return acc
            }, {})
          },
          overwrite: overwriteExisting,
          location: locationId
        }
      })
      onConfirm()
    } catch (error) {
      Sentry.captureException(error)
      enqueueSnackbar('error', { variant: 'error' })
    }
    setIsLoading(false)
  }
}

export default TransferToLocationDialog
